<!-- ===================================================================
     category-review.component.html
     Module 7 (Loading) + Module 8 (Category Selection dual-panel UI)
     =================================================================== -->

<!-- ── LOADING STATE — 3-phase animated loader ───────────────────────── -->
<div class="loader-overlay" *ngIf="pageState() === 'loading'" aria-live="polite">

  <!-- Header -->
  <div class="loader-header">
    <h2 class="loader-header__title">Analysing Your Application</h2>
    <p class="loader-header__sub">
      ☕ Sit back and relax — this may take several minutes depending on the
      number and size of your documents.
    </p>
  </div>

  <!-- Overall spinner + % -->
  <div class="spinner-wrap">
    <svg class="spinner" viewBox="0 0 56 56" fill="none"
         xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <circle cx="28" cy="28" r="24" stroke="#E6E6E6" stroke-width="5"/>
      <circle cx="28" cy="28" r="24"
              stroke="url(#spinGrad)" stroke-width="5"
              stroke-linecap="round"
              stroke-dasharray="150.8"
              stroke-dashoffset="37.7"
              class="spinner__arc"/>
      <defs>
        <linearGradient id="spinGrad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%"   stop-color="#0056A2"/>
          <stop offset="100%" stop-color="#19E3C5"/>
        </linearGradient>
      </defs>
    </svg>
    <span class="spinner__pct">{{ overallProgress() }}%</span>
  </div>

  <!-- Overall progress bar -->
  <div class="loader-bar-row">
    <div class="loader-bar">
      <div class="loader-bar__fill" [style.width.%]="overallProgress()"></div>
    </div>
  </div>

  <!-- ── Phase cards ──────────────────────────────────────────────────── -->
  <div class="phase-list">

    <!-- Phase 1 — Documents Uploaded (always done) -->
    <div class="phase-card phase-card--done">
      <div class="phase-icon">✅</div>
      <div class="phase-body">
        <div class="phase-row">
          <span class="phase-num">Phase 1</span>
          <span class="phase-badge phase-badge--done">Complete</span>
        </div>
        <span class="phase-title">Documents Uploaded</span>
        <span class="phase-detail">Your files are safely stored and ready for processing.</span>
      </div>
    </div>

    <!-- Phase 2 — Content Extraction -->
    <div class="phase-card"
         [class.phase-card--active]="currentPhase() === 'extraction'"
         [class.phase-card--done]="extractionDone()">

      <!-- Icon: spinner when active, checkmark when done, clock when pending -->
      <div class="phase-icon" *ngIf="extractionDone()">✅</div>
      <div class="phase-icon phase-icon--spin" *ngIf="currentPhase() === 'extraction' && !extractionDone()">⚙️</div>
      <div class="phase-icon phase-icon--pending" *ngIf="currentPhase() === 'analysis' && !extractionDone()">⏳</div>

      <div class="phase-body">
        <div class="phase-row">
          <span class="phase-num">Phase 2</span>
          <span class="phase-badge phase-badge--done"    *ngIf="extractionDone()">Complete</span>
          <span class="phase-badge phase-badge--active"  *ngIf="currentPhase() === 'extraction' && !extractionDone()">In Progress</span>
          <span class="phase-badge phase-badge--pending" *ngIf="currentPhase() === 'analysis' && !extractionDone()">Pending</span>
        </div>
        <span class="phase-title">Content Extraction</span>
        <span class="phase-detail">{{ formatMessage(extractionMessage()) }}</span>

        <!-- Detail / file count -->
        <span class="phase-subdetail" *ngIf="extractionDetail()">
          📁 {{ extractionDetail() }}
        </span>



        <!-- Mini progress bar (visible only while active) -->
        <div class="phase-mini-row" *ngIf="currentPhase() === 'extraction' && !extractionDone()">
          <div class="phase-mini-bar">
            <div class="phase-mini-bar__fill" [style.width.%]="extractionProgress()"></div>
          </div>
          <span class="phase-mini-pct">{{ extractionProgress() }}%</span>
        </div>
      </div>
    </div>

    <!-- Phase 3 — AI Guardrail Analysis -->
    <div class="phase-card"
         [class.phase-card--active]="currentPhase() === 'analysis'"
         [class.phase-card--done]="analysisDone()">

      <div class="phase-icon" *ngIf="analysisDone()">✅</div>
      <div class="phase-icon phase-icon--spin" *ngIf="currentPhase() === 'analysis' && !analysisDone()">🤖</div>
      <div class="phase-icon phase-icon--pending" *ngIf="currentPhase() === 'extraction'">⏳</div>

      <div class="phase-body">
        <div class="phase-row">
          <span class="phase-num">Phase 3</span>
          <span class="phase-badge phase-badge--done"    *ngIf="analysisDone()">Complete</span>
          <span class="phase-badge phase-badge--active"  *ngIf="currentPhase() === 'analysis' && !analysisDone()">In Progress</span>
          <span class="phase-badge phase-badge--pending" *ngIf="currentPhase() === 'extraction'">Pending</span>
        </div>
        <span class="phase-title">AI Guardrail Category Analysis</span>
        <span class="phase-detail">{{ formatMessage(analysisMessage()) }}</span>

        <!-- Mini progress bar (visible only while active) -->
        <div class="phase-mini-row" *ngIf="currentPhase() === 'analysis' && !analysisDone()">
          <div class="phase-mini-bar">
            <div class="phase-mini-bar__fill" [style.width.%]="analysisProgress()"></div>
          </div>
          <span class="phase-mini-pct">{{ analysisProgress() }}%</span>
        </div>

        <!-- Pending hint -->
        <span class="phase-subdetail phase-subdetail--muted"
              *ngIf="currentPhase() === 'extraction'">
          🧠 The AI will start once extraction completes — won't be long!
        </span>
      </div>
    </div>

  </div><!-- /phase-list -->

</div><!-- /loader-overlay -->


<!-- ── ERROR STATE ────────────────────────────────────────────────────── -->
<div class="cat-page" *ngIf="pageState() === 'error'">
  <div class="error-shell">
    <div class="error-icon-wrap">
      <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg"
           class="error-icon" aria-hidden="true">
        <circle cx="32" cy="32" r="28" stroke="#E6192F" stroke-width="2" opacity="0.2"/>
        <circle cx="32" cy="32" r="22" stroke="#E6192F" stroke-width="2"/>
        <path d="M32 20v14" stroke="#E6192F" stroke-width="2.5" stroke-linecap="round"/>
        <circle cx="32" cy="42" r="2" fill="#E6192F"/>
      </svg>
    </div>
    <h2 class="error-heading">Analysis Failed</h2>
    <p class="error-msg">{{ errorMessage() }}</p>
    <div class="error-actions">
      <button class="btn btn--ghost" (click)="goBack()">← Back to Upload</button>
      <button class="btn btn--primary" (click)="retryAnalysis()">Retry Analysis</button>
    </div>
  </div>
</div><!-- /error -->


<!-- ── READY STATE — MODULE 8 CATEGORY SELECTION ─────────────────────── -->
<div class="cat-sel-page" *ngIf="pageState() === 'ready'">

  <!-- ── Page header ──────────────────────────────────────────────────── -->
  <header class="cat-sel-header">
    <h1 class="cat-sel-header__title">Category Selection</h1>
    <p class="cat-sel-header__sub">
      Based on your document analysis, we've suggested relevant categories. You can
      add or remove categories for your analysis.
    </p>
  </header>

  <!-- ── Main dual-panel layout ───────────────────────────────────────── -->
  <div class="cat-panels">

    <!-- ══ LEFT PANEL — Available Categories ══════════════════════════ -->
    <div class="panel panel--left">
      <div class="panel__head">
        <h2 class="panel__title">Available Categories</h2>
        <p class="panel__sub">Click the + button to add categories to your analysis</p>
      </div>

      <!-- Category list -->
      <ul class="avail-list" role="list">
        <li
          *ngFor="let name of filteredAvailableList(); trackBy: trackByName"
          class="avail-item"
          [class.avail-item--ai]="hasAiData(name)">

          <!-- Name row -->
          <div class="avail-item__row">
            <span class="avail-item__name">{{ name }}</span>

            <!-- AI badge (only for AI-suggested categories) -->
            <button
              *ngIf="hasAiData(name)"
              class="avail-item__ai-badge"
              [class.avail-item__ai-badge--open]="isAvailAiOpen(name)"
              (click)="toggleAvailAiPopover($event, name)"
              [attr.aria-expanded]="isAvailAiOpen(name)"
              [attr.aria-label]="'Show AI suggestion details for ' + name"
              title="View AI suggestion details">
              <span class="badge-text">AI {{ getAiData(name)?.confidence_score }}%</span>
              <svg class="badge-arrow" viewBox="0 0 10 6" fill="none"
                   xmlns="http://www.w3.org/2000/svg" aria-hidden="true"
                   [class.badge-arrow--up]="isAvailAiOpen(name)">
                <path d="M1 1l4 4 4-4" stroke="currentColor" stroke-width="1.5"
                      stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>

            <button
              class="avail-item__add-btn"
              (click)="addCategory(name)"
              [attr.aria-label]="'Add ' + name + ' to selected categories'"
              title="Add to selected categories">
              <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"
                   aria-hidden="true">
                <circle cx="10" cy="10" r="9" fill="#2FA1D3"/>
                <path d="M10 6v8M6 10h8" stroke="#fff" stroke-width="2"
                      stroke-linecap="round"/>
              </svg>
            </button>
          </div>

          <!-- AI detail popover (inline, same structure as selected-panel dropdown) -->
          <div class="avail-ai-detail" *ngIf="hasAiData(name) && isAvailAiOpen(name)"
               role="region" [attr.aria-label]="'AI details for ' + name">

            <div class="ai-dropdown__section">
              <h4 class="ai-dropdown__title">
                <svg class="ai-dropdown__title-icon" viewBox="0 0 14 14" fill="none"
                     xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <circle cx="7" cy="7" r="6" stroke="#0056A2" stroke-width="1.2"/>
                  <path d="M7 6v4M7 4v.5" stroke="#0056A2" stroke-width="1.4"
                        stroke-linecap="round"/>
                </svg>
                Summary:
              </h4>
              <p class="ai-dropdown__text">{{ getAiData(name)?.summary }}</p>
            </div>

            <div class="ai-dropdown__section" *ngIf="getAiData(name)?.proof?.referenced_text">
              <h4 class="ai-dropdown__title">
                <svg class="ai-dropdown__title-icon" viewBox="0 0 14 14" fill="none"
                     xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <rect x="1" y="1" width="12" height="12" rx="2"
                        stroke="#0056A2" stroke-width="1.2"/>
                  <path d="M3.5 5h7M3.5 8h5" stroke="#0056A2" stroke-width="1.2"
                        stroke-linecap="round"/>
                </svg>
                Evidence from Documents:
              </h4>
              <ul class="ai-dropdown__evidence-list">
                <li *ngFor="let s of splitEvidence(getAiData(name)!.proof.referenced_text)">
                  {{ s }}.
                </li>
              </ul>
              <p class="ai-dropdown__source" *ngIf="getAiData(name)?.proof?.file_name">
                📄 <strong>{{ truncateFileName(getAiData(name)!.proof.file_name) }}</strong>
                <span *ngIf="getAiData(name)?.proof?.page_number">
                  — Page {{ getAiData(name)?.proof?.page_number }}
                </span>
              </p>
            </div>

          </div><!-- /avail-ai-detail -->

        </li>

        <!-- Empty: all added -->
        <li class="avail-list__empty"
            *ngIf="filteredAvailableList().length === 0 && availableList().length === 0">
          <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg"
               class="avail-list__empty-icon" aria-hidden="true">
            <circle cx="20" cy="20" r="16" stroke="#19E3C5" stroke-width="1.5" opacity="0.6"/>
            <path d="M13 20l5 5 9-9" stroke="#19E3C5" stroke-width="2"
                  stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <p>All categories have been added</p>
        </li>

        <!-- Empty: search no-results -->
        <li class="avail-list__empty"
            *ngIf="filteredAvailableList().length === 0 && availableList().length > 0">
          <p>No categories match "<em>{{ leftSearchQuery }}</em>"</p>
        </li>
      </ul>
    </div><!-- /panel--left -->


    <!-- ══ RIGHT PANEL — Selected Categories ══════════════════════════ -->
    <div class="panel panel--right">
      <div class="panel__head">
        <h2 class="panel__title panel__title--right">Selected Categories for Analysis</h2>
        <p class="panel__sub">
          AI suggested categories are automatically added.
          Click the &minus; button to remove categories
        </p>
      </div>

      <!-- Selected list -->
      <ul class="sel-list" role="list">

        <li
          *ngFor="let entry of selectedList(); let i = index; trackBy: trackByEntryName"
          class="sel-card"
          [class.sel-card--ai]="entry.type === 'ai'"
          [class.sel-card--manual]="entry.type === 'manual'">

          <!-- Card header row -->
          <div class="sel-card__row">
            <span class="sel-card__name">{{ entry.name }}</span>

            <!-- AI badge (dropdown trigger) — only for AI entries -->
            <button
              *ngIf="isAi(entry)"
              class="sel-card__ai-badge"
              [class.sel-card__ai-badge--open]="entry.dropdownOpen"
              (click)="toggleAiDropdown($event, i)"
              [attr.aria-expanded]="entry.dropdownOpen"
              [attr.aria-label]="'AI analysis details for ' + entry.name">
              <span class="badge-text">
                Confidence ({{ entry.aiData.confidence_score }}%)
              </span>
              <svg class="badge-arrow" viewBox="0 0 10 6" fill="none"
                   xmlns="http://www.w3.org/2000/svg" aria-hidden="true"
                   [class.badge-arrow--up]="entry.dropdownOpen">
                <path d="M1 1l4 4 4-4" stroke="currentColor" stroke-width="1.5"
                      stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>

            <!-- Remove button -->
            <button
              class="sel-card__remove-btn"
              (click)="removeCategory(entry.name)"
              [attr.aria-label]="'Remove ' + entry.name + ' from selection'"
              title="Remove from selection">
              <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"
                   aria-hidden="true">
                <circle cx="10" cy="10" r="9" fill="#E6192F"/>
                <path d="M7 10h6" stroke="#fff" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </button>
          </div><!-- /sel-card__row -->

          <!-- ── AI detail dropdown ──────────────────────────────────── -->
          <div class="ai-dropdown" *ngIf="isAi(entry) && entry.dropdownOpen"
               role="region" [attr.aria-label]="'Details for ' + entry.name">

            <!-- Section 1: Summary -->
            <div class="ai-dropdown__section">
              <h4 class="ai-dropdown__title">
                <svg class="ai-dropdown__title-icon" viewBox="0 0 14 14" fill="none"
                     xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <circle cx="7" cy="7" r="6" stroke="#0056A2" stroke-width="1.2"/>
                  <path d="M7 6v4M7 4v.5" stroke="#0056A2" stroke-width="1.4"
                        stroke-linecap="round"/>
                </svg>
                Summary:
              </h4>
              <p class="ai-dropdown__text">{{ entry.aiData.summary }}</p>
            </div>

            <!-- Section 2: Evidence from Documents -->
            <div class="ai-dropdown__section" *ngIf="entry.aiData.proof?.referenced_text">
              <h4 class="ai-dropdown__title">
                <svg class="ai-dropdown__title-icon" viewBox="0 0 14 14" fill="none"
                     xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <rect x="1" y="1" width="12" height="12" rx="2"
                        stroke="#0056A2" stroke-width="1.2"/>
                  <path d="M3.5 5h7M3.5 8h5" stroke="#0056A2" stroke-width="1.2"
                        stroke-linecap="round"/>
                </svg>
                Evidence from Documents:
              </h4>
              <ul class="ai-dropdown__evidence-list">
                <li *ngFor="let sentence of splitEvidence(entry.aiData.proof.referenced_text)">
                  {{ sentence }}.
                </li>
                <li *ngIf="splitEvidence(entry.aiData.proof.referenced_text).length === 0">
                  {{ entry.aiData.proof.referenced_text }}
                </li>
              </ul>
              <p class="ai-dropdown__source" *ngIf="entry.aiData.proof.file_name">
                📄 <strong>{{ truncateFileName(entry.aiData.proof.file_name) }}</strong>
                <span *ngIf="entry.aiData.proof.page_number">
                  — Page {{ entry.aiData.proof.page_number }}
                </span>
              </p>
            </div>

          </div><!-- /ai-dropdown -->

        </li><!-- /sel-card -->

        <!-- Empty state -->
        <li class="sel-list__empty" *ngIf="selectedList().length === 0">
          <svg viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg"
               class="sel-list__empty-icon" aria-hidden="true">
            <circle cx="30" cy="30" r="26" stroke="#E6E6E6" stroke-width="2"/>
            <path d="M20 30h20M30 20v20" stroke="#5F6A72" stroke-width="2"
                  stroke-linecap="round" opacity="0.4"/>
          </svg>
          <p>No categories selected yet.<br/>
            <span>Add categories using the + button on the left.</span>
          </p>
        </li>

      </ul>
    </div><!-- /panel--right -->

  </div><!-- /cat-panels -->

  <!-- ── Bottom action buttons ────────────────────────────────────────── -->
  <div class="cat-sel-actions">
    <button class="btn btn--ghost" (click)="goBack()">
      ← Back to Upload
    </button>
    <button
      class="btn btn--primary"
      [disabled]="selectedCount() === 0"
      [class.btn--disabled]="selectedCount() === 0"
      (click)="proceed()"
      [title]="selectedCount() === 0 ? 'Select at least one category to continue' : ''">
      Continue to Analysis →
    </button>
  </div>

  <!-- ── AI Disclaimer ────────────────────────────────────────────────── -->
  <p class="ai-disclaimer">
    Content generated by AI may be inaccurate. Please review all selections before proceeding.
  </p>

</div><!-- /cat-sel-page -->
